import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Lottie from "lottie-react";
import images from "../../images";
import OpacityPage from "../../layout/v6/OpacityPage";
import {
  useGetQuestDetailsPublicQuery,
  useLazyGetValidateReferralCodeQuery,
} from "../../services/apiSlice";
import LoadingCommon from "../../components/common/LoadingStatus/LoadingCommon";

const InvitationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [isValidReferral, setIsValidReferral] = useState(false);
  const [referralData, setReferralData] = useState(null);

  // Get quest_id from query params
  const searchParams = new URLSearchParams(location.search);
  const locationId = searchParams.get("location_id");
  const questId = searchParams.get("quest_id");
  const referralCode = searchParams.get("code");

  const accessToken =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  // Fetch quest details if quest_id exists

  const [getValidateReferralCode, { isFetching: isValidatingReferral }] =
    useLazyGetValidateReferralCodeQuery();
  const { data: questData } = useGetQuestDetailsPublicQuery(
    { quest_id: questId },
    { skip: !questId },
  );

  const shopName =
    questData?.data?.quest_business?.business?.company_name || "shop";

  useEffect(() => {
    const validateReferral = async () => {
      if (!referralCode) return;

      setIsLoading(true);
      try {
        const response = await getValidateReferralCode({
          code: referralCode,
        }).unwrap();

        if (response?.status === 200 && response?.data?.is_valid === true) {
          setIsValidReferral(true);
          setReferralData(response?.data?.data);

          // Lưu referral code và info để dùng sau (khi hoàn thành quest)
          sessionStorage.setItem("referralCode", referralCode);
          sessionStorage.setItem(
            "referralInfo",
            JSON.stringify({
              referral_name: response?.data?.data?.referral_name,
              referral_message: response?.data?.data?.referral_message,
              expire_at: response?.data?.data?.expire_at,
            }),
          );
        } else {
          setIsValidReferral(false);
        }
      } catch (_err) {
        setIsValidReferral(false);
      } finally {
        setIsLoading(false);
      }
    };

    validateReferral();
  }, [referralCode, getValidateReferralCode]);

  const handleActionClick = () => {
    if (accessToken) {
      if (locationId) {
        navigate(`/quest/location/${locationId}`, { replace: true });
      } else {
        navigate("/quest");
      }
    } else {
      navigate(`/register${location.search}`);
    }
  };

  if (isLoading || isValidatingReferral) {
    return (
      <OpacityPage isOpen={true} backgroundColor="#fff">
        <main className="max-w-[480px] h-full flex items-center justify-center">
          <LoadingCommon statusLoading={true} />
        </main>
      </OpacityPage>
    );
  }

  return (
    <OpacityPage isOpen={true} backgroundColor="#fff">
      <main className="max-w-[480px] h-full">
        <Helmet>
          <meta name="theme-color" content="#ffffff" />
        </Helmet>
        <div className="flex flex-col py-[72px] px-[40px] gap-[20px] xs:gap-[60px]">
          {/* Logo Section */}
          <section className="flex w-full">
            <img
              src={images.LogoLogin}
              className="w-[80px] h-[60px] aspect-[4/3] max-[380px]:w-[64px] max-[380px]:h-[48px] object-contain"
              alt="Rally Logo"
            />
          </section>

          {/* Content Section */}
          <section className="flex flex-col flex-1 gap-[20px] xs:gap-[60px]">
            {/* Headline */}
            {referralCode ? (
              <>
                <h1 className="text-[#253FF6] text-[24px] xs:text-[36px] font-bold leading-tight text-left">
                  {referralData?.referral_name}{" "}
                  {isValidReferral
                    ? t("invitationModal.invitingYou")
                    : t("invitationModal.hadInvitedYou")}
                  <br />
                  {t("invitationModal.toBecomeMysteryShopper")}
                </h1>
                {isValidReferral ? (
                  <div className="flex flex-col gap-4 text-[14px] xs:text-[20px] font-medium text-left">
                    <p className="text-[14px] xs:text-lg font-medium text-[#253FF6]">
                      {t("invitationModal.welcomeToRally")},
                    </p>
                    <p className="text-[14px] xs:text-lg font-medium text-[#253FF6]">
                      {t("invitationModal.fewMinutes")}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4 text-[14px] xs:text-[20px] font-medium text-left">
                    <p className="text-[14px] xs:text-lg font-medium text-[#253FF6]">
                      {t("invitationModal.expiredInvitation")},
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col gap-6">
                <h1 className="text-[#253FF6] text-[24px] xs:text-[36px] font-bold leading-tight text-left whitespace-pre-line">
                  {shopName} {t("invitationModal.invitesYou")} <br />
                  {t("invitationModal.toBeTheir")} <br />
                  {t("invitationModal.mysteryShopper")}
                </h1>
                <div className="flex flex-col gap-4 text-[14px] xs:text-[20px] font-medium text-left">
                  <p className="text-[14px] xs:text-lg font-medium text-[#253FF6]">
                    {t("invitationModal.welcomeToRally")},
                  </p>
                  <p className="text-[14px] xs:text-lg font-medium text-[#253FF6]">
                    {t("invitationModal.fewMinutes")}
                  </p>
                </div>
              </div>
            )}
            <div className="flex flex-col gap-6">
              {/* Description Text */}

              {/* Illustration Image / Animation */}
              <figure className="flex justify-center items-center w-full flex-1 min-h-[150px]">
                <div className="w-[200px]">
                  {/* Ensure the path to your JSON animation file is correct */}
                  <Lottie
                    animationData={require("../../images/animation/select.json")}
                    loop={false}
                    autoplay={true}
                  />
                </div>
              </figure>

              {/* Action Button */}
              <div className="w-full mt-auto">
                <button
                  onClick={handleActionClick}
                  className="w-full px-4 py-3 bg-[#253FF6] hover:bg-[#1e32cc] text-white rounded-lg text-[14px] xs:text-[20px] font-medium transition-all shadow-sm max-[380px]:text-base max-[380px]:py-2"
                >
                  {accessToken
                    ? t("base.next")
                    : t("invitationModal.letsSignUp") || "Let's Sign Up"}
                </button>
              </div>
            </div>
          </section>
        </div>
      </main>
    </OpacityPage>
  );
};

export default InvitationPage;
